<template>
  <v-app id="ism-app" :class="{ 'dark-mode': isDark, 'sb-collapsed': (sidebarCollapsed && !isMobile) || isMobile }">
    <!-- ══════════ SIDEBAR ══════════ -->
    <aside
      class="ism-sidebar"
      :class="{
        'is-collapsed': sidebarCollapsed && !isMobile,
        'is-mobile': isMobile,
        'is-mobile-open': isMobile && mobileSidebarOpen
      }"
    >
      <div class="ism-sb-header">
        <router-link to="/superadmin/dashboard" class="ism-sb-logo" @click.native="closeMobileSidebar">
          <div class="ism-logo-container">
            <img
              :src="isDark ? '/favicon_white.ico' : '/favicon.svg'"
              alt="L"
              class="ism-logo-img ism-logo-img--small"
            />
            <img
              :src="isDark ? '/WhiteLogo.svg' : '/LogoSvg.svg'"
              alt="iSmartSociety"
              class="ism-logo-img ism-logo-img--large"
            />
          </div>
        </router-link>
        <button
          v-if="isMobile"
          type="button"
          class="ism-sb-close"
          aria-label="Close menu"
          @click="closeMobileSidebar"
        >
          <v-icon size="22" color="var(--text-primary)">mdi-close</v-icon>
        </button>
      </div>

      <button
        v-if="!isMobile"
        class="ism-sb-toggle"
        @click="sidebarCollapsed = !sidebarCollapsed"
        :title="sidebarCollapsed ? 'Expand' : 'Collapse'"
      >
        <v-icon size="14" class="ism-toggle-icon">{{
          sidebarCollapsed ? "mdi-chevron-right" : "mdi-chevron-left"
        }}</v-icon>
      </button>

      <nav class="ism-sb-nav">
        <template v-for="item in navigationItems">
          <!-- Single item -->
          <v-tooltip
            v-if="!item.childrens"
            right
            content-class="tooltip-right"
            :key="item.title"
            nudge-right="10"
            open-delay="400"
            :disabled="isMobile || !sidebarCollapsed"
          >
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <router-link
                  :to="item.route"
                  class="ism-sb-item"
                  active-class="ism-sb-item--active"
                  @click.native="closeMobileSidebar"
                >
                  <span class="ism-sb-item-icon"
                    ><v-icon size="20">{{ item.icon }}</v-icon></span
                  >
                  <span class="ism-sb-item-label">{{ item.title }}</span>
                </router-link>
              </div>
            </template>
            <span>{{ item.title }}</span>
          </v-tooltip>

          <!-- Group item -->
          <div v-else :key="'g_' + item.title" class="ism-sb-group">
            <v-tooltip
              right
              content-class="tooltip-right"
              nudge-right="10"
              open-delay="400"
              :disabled="isMobile || !sidebarCollapsed"
            >
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="ism-sb-item ism-sb-group-header"
                  :class="{ 'ism-sb-item--active': item.active }"
                  @click="toggleGroup(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <span class="ism-sb-item-icon"
                    ><v-icon size="20">{{ item.icon }}</v-icon></span
                  >
                  <span class="ism-sb-item-label">{{ item.title }}</span>
                  <v-icon
                    size="14"
                    class="ism-sb-chevron"
                    :class="{ 'is-open': item.active }"
                    >mdi-chevron-down</v-icon
                  >
                </div>
              </template>
              <span>{{ item.title }}</span>
            </v-tooltip>

            <transition name="ism-sub-slide">
              <div v-if="item.active" class="ism-sb-subnav">
                <v-tooltip
                  v-for="sub in item.childrens"
                  :key="sub.title"
                  right
                  content-class="tooltip-right"
                  nudge-right="10"
                  open-delay="400"
                  :disabled="isMobile || !sidebarCollapsed"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <router-link
                        :to="sub.route"
                        class="ism-sb-subitem"
                        active-class="ism-sb-subitem--active"
                        @click.native="closeMobileSidebar"
                      >
                        <v-icon size="16" class="ism-sb-sub-icon">{{
                          sub.icon
                        }}</v-icon>
                        <span class="ism-sb-sub-label">{{ sub.title }}</span>
                      </router-link>
                    </div>
                  </template>
                  <span>{{ sub.title }}</span>
                </v-tooltip>
              </div>
            </transition>
          </div>
        </template>
      </nav>
    </aside>

    <!-- Tap outside sidebar to close (mobile) -->
    <div
      v-if="isMobile && mobileSidebarOpen"
      class="ism-sidebar-scrim"
      aria-hidden="true"
      @click="closeMobileSidebar"
    ></div>

    <!-- ══════════ TOPBAR ══════════ -->
    <header
      class="ism-topbar"
      :style="{ left: isMobile ? '0px' : (sidebarCollapsed ? '76px' : '260px') }"
    >
      <div class="ism-topbar-left">
        <!-- Hamburger button on mobile -->
        <button
          v-if="isMobile"
          class="ism-mobile-toggle"
          @click="mobileSidebarOpen = !mobileSidebarOpen"
          title="Toggle Navigation"
        >
          <v-icon size="22" color="var(--text-primary)">mdi-menu</v-icon>
        </button>

        <div class="ism-topbar-page-title">
          <span class="ism-welcome-txt">Welcome,</span>
          <span class="ism-user-name">{{ user.name }}</span>
          <span class="ism-role-tag">Admin User</span>
        </div>
      </div>

      <div class="ism-topbar-right">
        <ThemeToggle />
        <div class="ism-divider-v"></div>
        <v-menu
          transition="slide-y-transition"
          bottom
          offset-y
          left
          min-width="220"
          content-class="ism-user-menu-pop"
        >
          <template v-slot:activator="{ on, attrs }">
            <div class="ism-avatar-btn" v-bind="attrs" v-on="on">
              <PremiumAvatar
                :image="
                  user.profile_url && !avatarError
                    ? $resolveUploadUrl('super_admins/profile_pics/' + user.profile_url)
                    : null
                "
                :name="user.name"
                :size="36"
              />
              <v-icon size="14" color="var(--text-secondary)"
                >mdi-chevron-down</v-icon
              >
            </div>
          </template>
          <div class="ism-user-menu-inner">
            <div class="ism-menu-user-hdr">
              <PremiumAvatar
                :image="
                  user.profile_url && !avatarError
                    ? $resolveUploadUrl('super_admins/profile_pics/' + user.profile_url)
                    : null
                "
                :name="user.name"
                :size="40"
              />
              <div style="min-width: 0; flex: 1;">
                <div class="ism-menu-name" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ user.name }}</div>
                <div class="ism-menu-mail" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" :title="user.email">{{ user.email }}</div>
              </div>
            </div>
            <div class="ism-menu-divider"></div>
            <button @click="showProfileModal = true" class="ism-menu-item">
              <v-icon size="18">mdi-account-circle-outline</v-icon> My Profile
            </button>
            <button @click="showPasswordModal = true" class="ism-menu-item">
              <v-icon size="18">mdi-key-outline</v-icon> Change Password
            </button>
            <div class="ism-menu-divider"></div>
            <button
              class="ism-menu-item ism-menu-item--danger"
              @click="confirmLogout = true"
            >
              <v-icon size="18">mdi-logout-variant</v-icon> Log Out
            </button>
          </div>
        </v-menu>
      </div>
    </header>

    <main
      class="ism-main-content"
      :class="{ 'sidebar-collapsed': sidebarCollapsed && !isMobile, 'is-mobile': isMobile }"
      :style="{ marginLeft: isMobile ? '0px' : (sidebarCollapsed ? '76px' : '260px') }"
    >
      <div class="ism-topbar-spacer"></div>
      <Alert />
      <ConfirmModal
        :isOpen="confirmLogout"
        type="logout"
        title="Logout"
        message="Are you sure you want to log out?"
        confirmText="Yes, Logout"
        cancelText="Stay"
        @confirm="logout"
        @cancel="confirmLogout = false"
      />
      <div class="ism-page-content">
        <router-view @changeProfilePic="changeProfilePic" />
      </div>
    </main>

    <Profile
      v-if="showProfileModal"
      v-model="showProfileModal"
      @changeProfilePic="changeProfilePic"
    />
    <ChangePassword v-if="showPasswordModal" v-model="showPasswordModal" />
  </v-app>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import Alert from "../../Alert.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import ThemeToggle from "../../common/ThemeToggle.vue";
import PremiumAvatar from "../../common/PremiumAvatar.vue";
import Profile from "../Profile.vue";
import ChangePassword from "../auth/ChangePassword.vue";

export default {
  name: "AdminLayout",
  components: {
    Alert,
    ConfirmModal,
    ThemeToggle,
    Profile,
    ChangePassword,
    PremiumAvatar,
  },
  data: () => ({
    sidebarCollapsed: false,
    mobileSidebarOpen: false,
    confirmLogout: false,
    avatarError: false,
    showProfileModal: false,
    showPasswordModal: false,
    user: { name: "", email: "", profile_url: null },
    isMobile: false,
    navigationItems: [
      {
        title: "Dashboard",
        icon: "mdi-view-dashboard-outline",
        route: "/superadmin/dashboard",
      },
      {
        title: "Society Requests",
        icon: "mdi-clipboard-text-clock-outline",
        route: "/superadmin/society/requests",
      },
      {
        title: "Society List",
        icon: "mdi-office-building-outline",
        route: "/superadmin/society/list",
      },
      {
        title: "Admin Users",
        icon: "mdi-shield-account-outline",
        route: "/superadmin/manage-admins",
      },
      {
        title: "Feedbacks",
        icon: "mdi-message-star-outline",
        route: "/superadmin/feedback",
      },
      {
        title: "Plans",
        icon: "mdi-tag-multiple-outline",
        route: "/superadmin/plan",
      },
      {
        title: "Payments",
        icon: "mdi-credit-card-outline",
        route: "/superadmin/payment",
      },
      {
        title: "FAQs",
        icon: "mdi-help-circle-outline",
        active: false,
        childrens: [
            {
            title: "FAQ Categories",
            icon: "mdi-tag-outline",
            route: "/superadmin/faq-categories",
          },
          {
            title: "FAQ List",
            icon: "mdi-frequently-asked-questions",
            route: "/superadmin/faqs",
          },
        
        ],
      },
    
      {
        title: "App Versions",
        icon: "mdi-cellphone-cog",
        route: "/superadmin/app-versions",
      },
      {
        title: "Broadcast Messages",
        icon: "mdi-bullhorn-outline",
        route: "/superadmin/broadcasts",
      },
      {
        title: "Contact Us",
        icon: "mdi-message-text-clock-outline",
        route: "/superadmin/contact-queries",
      },
        {
        title: "Settings",
        icon: "mdi-cog-outline",
        route: "/superadmin/settings",
      },
      {
        title: "CMS",
        icon: "mdi-file-document-edit-outline",
        active: false,
        childrens: [
          {
            title: "Privacy Policy",
            icon: "mdi-shield-lock-outline",
            route: "/superadmin/privacy",
          },
          {
            title: "Terms & Condition",
            icon: "mdi-script-text-outline",
            route: "/superadmin/term_condition",
          },
          {
            title: "Refund Policy",
            icon: "mdi-cash-refund",
            route: "/superadmin/refund_policy",
          },
        ],
      },
    ],
  }),
  watch: {
    $route() {
      this.syncActiveGroup();
      if (this.isMobile) {
        this.mobileSidebarOpen = false;
      }
    },
    isDark(val) {
      if (this.$vuetify) this.$vuetify.theme.dark = val;
    },
  },
  computed: {
    ...mapState("theme", ["isDark"]),
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    closeMobileSidebar() {
      if (this.isMobile) this.mobileSidebarOpen = false;
    },
    toggleGroup(item) {
      this.navigationItems.forEach((n) => {
        if (n.childrens && n !== item) n.active = false;
      });
      item.active = !item.active;
    },
    onEscapeKey(e) {
      if (e.key === 'Escape' && this.isMobile && this.mobileSidebarOpen) {
        this.closeMobileSidebar();
      }
    },
    logout() {
      this.confirmLogout = false;
      this.$axios.post("/superadmin/logout").then((res) => {
        if (res.data.status) {
          localStorage.removeItem("user");
          this.$router.push("/superadmin/login");
        }
      });
    },
    changeProfilePic() {
      const user = JSON.parse(localStorage.getItem("user"));
      this.user.profile_url = user.profile_url;
    },
    syncActiveGroup() {
      const path = this.$route.path;
      this.navigationItems.forEach((item) => {
        if (item.childrens)
          item.active = item.childrens.some((c) => path.startsWith(c.route));
      });
    },
    checkMobile() {
      this.isMobile = window.innerWidth < 960;
      if (this.isMobile) {
        this.sidebarCollapsed = true;
      } else {
        this.sidebarCollapsed = window.innerWidth < 1200;
      }
    },
    onResize() {
      this.checkMobile();
    },
  },
  mounted() {
    const stored = localStorage.getItem("user");
    if (stored) this.user = JSON.parse(stored);
    this.syncActiveGroup();
    if (this.$vuetify) this.$vuetify.theme.dark = this.isDark;

    // Set professional tab title
    document.title = "iSmartSociety | Super Admin";

    // Update Favicon
    const link = document.querySelector("link[rel~='icon']");
    if (link) link.href = "/favicon_white.ico";

    // Sync body class
    if (this.isDark) document.body.classList.add("dark-mode");
    else document.body.classList.remove("dark-mode");

    this.checkMobile();
    window.addEventListener("resize", this.onResize);
    document.addEventListener("keydown", this.onEscapeKey);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("keydown", this.onEscapeKey);
  },
};
</script>

<style>
/* ── Global ── */
:root {
  --sidebar-width: 260px;
  --sidebar-collapsed: 76px;
  --topbar-height: 64px;
}

/* ── Sidebar ── */
.ism-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: var(--sidebar-width);
  background: var(--sidebar-bg);
  border-right: 1px solid var(--sidebar-bdr);
  display: flex;
  flex-direction: column;
  z-index: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.ism-sidebar.is-collapsed {
  width: var(--sidebar-collapsed);
}

.ism-sb-header {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: var(--topbar-height);
  border-bottom: 1px solid var(--sidebar-bdr);
}

.ism-sb-logo {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  padding: 0 16px;
}

.ism-sb-close {
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-right: 10px;
  border: none;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s;
}

.ism-sb-close:hover {
  background: var(--border-color);
}
.ism-logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.ism-logo-img {
  transition: opacity 0.2s, transform 0.2s;
  object-fit: contain;
}
.ism-logo-img--small {
  width: 32px;
  height: 32px;
  position: absolute;
  opacity: 0;
  transform: scale(0.8);
}
.ism-logo-img--large {
  max-width: 140px;
  max-height: 36px;
  opacity: 1;
}

.is-collapsed .ism-logo-img--small {
  opacity: 1;
  transform: scale(1);
}
.is-collapsed .ism-logo-img--large {
  opacity: 0;
  transform: scale(0.8);
}

.ism-sb-toggle {
  position: absolute;
  top: 50px;
  right: -12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 501;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}
.ism-sb-toggle:hover {
  background: var(--accent);
  border-color: var(--accent);
}
.ism-sb-toggle:hover .v-icon {
  color: #fff !important;
}

.ism-sb-nav {
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;
  scrollbar-width: none;
}
.ism-sb-nav::-webkit-scrollbar {
  display: none;
}

.ism-sb-item {
  display: flex;
  align-items: center;
  height: 44px;
  margin: 2px 12px;
  padding: 0 12px;
  border-radius: 10px;
  text-decoration: none;
  color: var(--sidebar-tx) !important;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  overflow: hidden;
}
.ism-sb-item:hover {
  background: rgba(0, 0, 0, 0.04);
  color: var(--accent) !important;
}
.ism-sb-item--active {
  background: var(--sidebar-act-bg) !important;
  color: var(--sidebar-act-tx) !important;
  font-weight: 600;
}

.ism-sb-item-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 12px;
}
.is-collapsed .ism-sb-item-icon {
  margin-right: 0;
  width: 100%;
}

.ism-sb-item-label {
  flex: 1;
  white-space: nowrap;
  transition: opacity 0.2s;
}
.is-collapsed .ism-sb-item-label,
.is-collapsed .ism-sb-chevron {
  opacity: 0;
  pointer-events: none;
  width: 0;
}

.ism-sb-chevron {
  transition: transform 0.2s;
  opacity: 0.5;
}
.ism-sb-chevron.is-open {
  transform: rotate(180deg);
}

.ism-sb-subnav {
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.02);
}
.ism-sb-subitem {
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 12px 0 48px;
  font-size: 13px;
  color: var(--sidebar-tx);
  text-decoration: none;
  transition: all 0.2s;
}
.is-collapsed .ism-sb-subitem {
  padding: 0;
  justify-content: center;
  height: 40px;
}
.ism-sb-sub-icon {
  margin-right: 12px;
  display: none;
}
.is-collapsed .ism-sb-sub-icon {
  display: flex;
  margin-right: 0;
}
.ism-sb-sub-label {
  white-space: nowrap;
}
.is-collapsed .ism-sb-sub-label {
  display: none;
}

.ism-sb-subitem:hover {
  color: var(--accent);
}
.ism-sb-subitem--active {
  color: var(--accent) !important;
  font-weight: 700;
}

/* ── Topbar ── */
.ism-topbar {
  position: fixed;
  top: 0;
  right: 0;
  height: var(--topbar-height);
  background: var(--card-bg);
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  z-index: 400;
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.ism-topbar-left {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}
.ism-topbar-page-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Inter", sans-serif;
  min-width: 0;
}
.ism-welcome-txt {
  color: var(--text-secondary);
  font-weight: 400;
  white-space: nowrap;
}
.ism-user-name {
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ism-role-tag {
  font-size: 10px;
  font-weight: 800;
  background: var(--accent-lt);
  color: var(--accent);
  padding: 3px 10px;
  border-radius: 6px;
  letter-spacing: 0.01em;
  margin-left: 10px;
  border: 1px solid rgba(242, 101, 34, 0.15);
  white-space: nowrap;
  flex-shrink: 0;
}

.ism-topbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}
.ism-divider-v {
  width: 1px;
  height: 24px;
  background: var(--border-color);
}

.ism-avatar-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 12px;
  transition: background 0.2s;
}
.ism-avatar-btn:hover {
  background: rgba(0, 0, 0, 0.03);
}
.ism-avatar-wrap {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
}
.ism-avatar-wrap img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* ── Content ── */
.ism-main-content {
  margin-left: var(--sidebar-width);
  min-height: 100vh;
  background: var(--bg-primary);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}
.ism-main-content.sidebar-collapsed {
  margin-left: var(--sidebar-collapsed);
}
.ism-topbar-spacer {
  height: var(--topbar-height);
}
.ism-page-content {
  padding: 24px;
  position: relative;
  z-index: 1;
  max-width: 100%;
  overflow-x: hidden;
}

#ism-app {
  background: var(--bg-primary) !important;
}

/* Modal Overlays Management */
.v-overlay__scrim {
  background-color: #0f172a !important; /* Deep blue-gray */
  opacity: 0.45; /* Allow Vuetify to override during transitions */
}
.dark-mode .v-overlay__scrim {
  background-color: #000 !important;
  opacity: 0.7;
}

/* ── Menu Popover ── */
.ism-user-menu-pop {
  border-radius: 16px !important;
  box-shadow: var(--shadow-lg) !important;
  border: 1px solid var(--border-color) !important;
}
.ism-user-menu-inner {
  background: var(--card-bg);
  overflow: hidden;
}
.ism-menu-user-hdr {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.ism-menu-avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: var(--accent-lt);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}
.ism-menu-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}
.ism-menu-mail {
  font-size: 12px;
  color: var(--text-secondary);
}
.ism-menu-divider {
  height: 1px;
  background: var(--border-color);
}
.ism-menu-item {
  width: 100%;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}
.ism-menu-item:hover {
  background: rgba(0, 0, 0, 0.03);
  color: var(--text-primary);
}
.ism-menu-item v-icon {
  color: inherit !important;
}
.ism-menu-item--danger {
  color: #ef4444 !important;
}
.ism-menu-item--danger:hover {
  background: rgba(239, 68, 68, 0.05);
}

/* ── Responsive ── */
@media (max-width: 1200px) {
  .ism-sb-toggle {
    display: none !important;
  }
}

/* Tablet & Mobile Layout overrides - Slide-out off-canvas drawer sidebar */
@media (max-width: 959px) {
  .ism-sidebar {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    bottom: 0 !important;
    width: min(var(--sidebar-width), 88vw) !important;
    max-width: 100% !important;
    transform: translateX(-100%);
    z-index: 1000 !important;
    box-shadow: none;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  .ism-sb-close {
    display: flex;
  }
  .ism-sidebar.is-mobile-open {
    transform: translateX(0) !important;
    box-shadow: var(--shadow-lg) !important;
  }
  .ism-logo-img--small {
    opacity: 0 !important;
    display: none !important;
  }
  .ism-logo-img--large {
    opacity: 1 !important;
    transform: scale(1) !important;
    display: block !important;
  }
  .ism-sb-item-label,
  .ism-sb-chevron {
    opacity: 1 !important;
    pointer-events: auto !important;
    width: auto !important;
  }
  
  /* Mobile Toggle Hamburger button styling */
  .ism-mobile-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    padding: 8px;
    margin-right: 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  .ism-mobile-toggle:hover {
    background-color: var(--border-color);
  }
  .ism-mobile-toggle:active {
    transform: scale(0.95);
  }
  
  /* Backdrop Scrim */
  .ism-sidebar-scrim {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.55);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 999;
    cursor: pointer;
    transition: opacity 0.3s ease;
  }
}

@media (max-width: 600px) {
  .ism-topbar {
    padding: 0 16px;
  }
  .ism-welcome-txt {
    display: none;
  }
  .ism-page-content {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .ism-role-tag {
    display: none !important;
  }
  .ism-user-name {
    font-size: 13px;
    max-width: min(28vw, 120px);
  }
  .ism-topbar-page-title {
    gap: 4px;
    min-width: 0;
  }
  .ism-topbar {
    padding: 0 10px !important;
  }
  .ism-topbar-right {
    gap: 8px !important;
  }
  .ism-avatar-btn {
    padding: 4px !important;
    gap: 4px !important;
    min-width: 44px;
    min-height: 44px;
  }
}

@media (max-width: 959px) {
  .ism-sb-item,
  .ism-sb-subitem {
    min-height: 44px;
  }
  .ism-mobile-toggle {
    min-width: 44px;
    min-height: 44px;
    padding: 10px;
  }
  .ism-main-content {
    overflow-x: hidden;
    max-width: 100vw;
  }
}
</style>
